import { Address } from '../../../../webmodule-common/interop/webmodule-interop';
import { buttonsSaveCancel } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../cache-impl/cache-registry';
import { customElement, property, state } from 'lit/decorators.js';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import { EventTemplate, Snippet } from '../../../../webmodule-common/other/ui/events';
import { getQuoteStatusStr } from '../../../quotes/data/quote-helper-functions';
import { html, TemplateResult } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { ModalDialog } from '../../../../webmodule-common/other/ui/modal-base';
import { ProjectPickerBrowser } from '../../../projects/views/project-picker';
import { ProjectState, ViewProjectSummary } from '../../../api/dealer-api-interface-project';
import { QuoteState } from '../../../api/dealer-api-interface-quote';
import { showValidations } from '../../../../webmodule-common/other/ui/modal-validationhandler';

import { userDataStore } from '../../common/current-user-data-store';
import {
  WebmoduleChangeEvent,
  WebmoduleRadioGroup
} from '../../../../webmodule-common/components/src/webmodule-components';
import { LangFranchiseeQuoteCreateProjectModal } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-create-project-modal-lang';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { AddressEditor } from '../../../../webmodule-common/other/ui/address-editor';
import { emptyAddress, isAddressNullOrEmpty } from '../../../../webmodule-common/other/ui/maps/map-helpers';
import { clone } from '../../../../webmodule-common/other/clone';

export interface FranchiseeQuoteCreateProjectModalOptions {
  clientId: string;
  title: string;
  description: string;
  defaultAddress: Address;
  shippingNotes: string | null;
  budget: number | null;
}

@customElement('wm-franchiseequotecreateprojectmodal')
export class FranchiseeQuoteCreateProjectModal extends ModalDialog {
  protected readonly dataBinding: DataBinding;
  protected readonly dataTracker: DataTracker;
  protected readonly clientId: string;
  protected readonly projectPicker: ProjectPickerBrowser;

  @property({ type: String })
  private _projectTitle: string = '';
  public get projectTitle(): string {
    return this._projectTitle;
  }

  protected set projectTitle(value: string) {
    this._projectTitle = value;
  }

  @property({ type: String })
  private _description: string = '';
  public get description(): string {
    return this._description;
  }

  protected set description(value: string) {
    this._description = value;
  }

  @property({ type: Object })
  private _projectAddress: Address;
  public get projectAddress(): Address {
    return this._projectAddress;
  }

  protected set projectAddress(value: Address) {
    this._projectAddress = value;
  }

  @property({ type: String })
  private _shippingNotes: string | null;
  public get shippingNotes(): string | null {
    return this._shippingNotes;
  }

  protected set shippingNotes(value: string | null) {
    this._shippingNotes = value;
  }

  @property({ type: Number })
  private _budget: number | null;
  public get budget(): number | null {
    return this._budget;
  }

  protected set budget(value: number | null) {
    this._budget = value;
  }

  @property({ type: Object })
  private _project: ViewProjectSummary | undefined;
  public get project(): ViewProjectSummary | undefined {
    return this._project;
  }

  protected set project(value: ViewProjectSummary) {
    this._project = value;
  }

  @state()
  private _ok: boolean = false;
  public get ok(): boolean {
    return this._ok;
  }

  protected set ok(value: boolean) {
    this._ok = value;
  }

  private _needsProjectCreate: boolean = true;
  public get needsProjectCreate(): boolean {
    return this._needsProjectCreate;
  }

  protected set needsProjectCreate(value: boolean) {
    this._needsProjectCreate = value;
  }

  constructor(options: FranchiseeQuoteCreateProjectModalOptions) {
    super();
    this.dataBinding = new DataBinding(this.ui);
    this.dataTracker = new DataTracker(this.dataBinding);

    this.clientId = options.clientId;
    this.projectTitle = options.title;
    this.description = options.description;
    this.projectAddress = options.defaultAddress;
    this.shippingNotes = options.shippingNotes;
    this.budget = options.budget;

    this.projectPicker = this.getPickerFactory();

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('projectTitle');
    addField('description');
    addField('budget', FieldType.money, true);
    addField('shippingNotes');
  }

  public async afterConstruction(): Promise<void> {
    await super.afterConstruction();

    await this.projectPicker.refreshData();
  }

  protected getTitle(): Snippet {
    return LangFranchiseeQuoteCreateProjectModal.newProject();
  }

  protected getPickerFactory(): ProjectPickerBrowser {
    return new ProjectPickerBrowser({
      projectStates: ProjectState.Active,
      clientCache: cache().client,
      userCache: cache().userProfile,
      purchaseOrderCache: cache().purchaseOrder,
      title: this.getTitle,
      // eslint-disable-next-line @typescript-eslint/require-await
      projectOwnerId: async () => userDataStore.defaultBranch.id,
      pageFragment: '',
      onProjectSelected: (projectSummary: ViewProjectSummary) => this.onProjectSelected(projectSummary),
      selectedProject: this.project,
      clientId: this.clientId
    });
  }

  protected bodyTemplate(): EventTemplate {
    const forms = new FormInputAssistant(this.dataTracker);

    const createRadioButton = (text: string, input: boolean) => {
      return html` <webmodule-radio value="${input}">${text}</webmodule-radio>`;
    };

    const selector = html`
      <webmodule-radio-group
        name="filtergroup-projectSetupType"
        class="quote-project-setup-filter"
        value="${this.needsProjectCreate}"
        inline
        size="small"
        @webmodule-change=${(e: WebmoduleChangeEvent) => this.onProjectSetupModeChanged(e)}
      >
        ${createRadioButton(LangFranchiseeQuoteCreateProjectModal.newProject(), true)}
        ${createRadioButton(LangFranchiseeQuoteCreateProjectModal.addToExistingProject(), false)}
      </webmodule-radio-group>
    `;

    return html`
      <form class="form-one-col frm-quote-create-project">
        <div class="row">
          <div class="form-column">
            <h3>
              ${LangFranchiseeQuoteCreateProjectModal.statusProgressesMessage({
                draft: getQuoteStatusStr(QuoteState.Draft),
                active: getQuoteStatusStr(QuoteState.Active)
              })}
            </h3>
            ${selector}
            <div class="quote-project-setup-content">
              ${this.needsProjectCreate ? this.createProjectTemplate(forms) : this.linkToExistingProjectTemplate()}
            </div>
          </div>
        </div>
      </form>
    `;
  }

  protected createProjectTemplate(forms: FormInputAssistant): TemplateResult {
    const addressTemplate = forms.addressEditor(
      LangFranchiseeQuoteCreateProjectModal.projectSiteAddress(),
      'projectAddressEditor',
      {
        address: this.projectAddress,
        shippingNotes: this.shippingNotes ?? undefined,
        onAddressChanged: (e: Event) => this.addressModified(e.currentTarget as AddressEditor),
        displayMap: false
      }
    );

    return html`
      ${forms.textRequired('projectTitle', LangFranchiseeQuoteCreateProjectModal.projectTitle(), 120)}
      ${forms.note('description', LangFranchiseeQuoteCreateProjectModal.description(), 2950)}
      ${forms.money('budget', LangFranchiseeQuoteCreateProjectModal.projectBudget())} ${addressTemplate}
    `;
  }

  protected linkToExistingProjectTemplate(): TemplateResult {
    return html`
      <div>${LangFranchiseeQuoteCreateProjectModal.onlyProjectsWithMatchingClient()}</div>
      ${this.projectPicker}
    `;
  }

  protected footerTemplate(): TemplateResult | null {
    const okEvent = async () => {
      this.dataTracker.applyChangeToValue();
      const errors = await this.getValidationErrors();
      if (errors.length > 0) {
        this.ok = false;
        await showValidations(errors);
      } else {
        this.ok = true;
        await this.hideModal();
      }
    };
    const cancelEvent = async () => {
      this.projectTitle = '';
      this.ok = false;
      await this.hideModal();
    };
    const buttons = buttonsSaveCancel();
    return this.createConfirmCancelButtons(buttons.ok, okEvent, buttons.cancel, cancelEvent);
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  protected async getValidationErrors(): Promise<string[]> {
    const errors: string[] = [];

    if (this.needsProjectCreate) {
      if (isEmptyOrSpace(this.dataBinding.getValue('projectTitle'))) {
        errors.push(LangFranchiseeQuoteCreateProjectModal.cannotHaveEmptyProjectTitle());
      }
      if (isAddressNullOrEmpty(this.projectAddress)) {
        errors.push(LangFranchiseeQuoteCreateProjectModal.cannotHaveEmptyProjectAddress());
      }
    } else {
      if (!this.project) {
        errors.push(LangFranchiseeQuoteCreateProjectModal.pleaseSelectProjectToAddQuote());
      }
    }

    return errors;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  protected async addressModified(addressEditor: AddressEditor) {
    if (addressEditor.address) {
      this.projectAddress = clone(addressEditor.address);
      this.shippingNotes = addressEditor.shippingNotes ?? null;
    } else {
      this.projectAddress = emptyAddress();
      this.shippingNotes = '';
    }
  }

  protected onProjectSelected(projectSummary: ViewProjectSummary): void {
    this.project = projectSummary;
  }

  protected onProjectSetupModeChanged(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleRadioGroup | null;

    if (target === null) return;

    const groupValue = target.value;

    this.needsProjectCreate = groupValue === 'true';

    this.requestUpdate();
  }
}
